import { CodeReferenceBlockHighlightRecord } from "models/view-models/code-reference-block-highlights/code-reference-block-highlight-record";
import { RefObject } from "react";
import { HighlightColor } from "utilities/enumerations/highlight-color";
import { DATA_SECTION_ID } from "utilities/hooks/domain/notebooks/code-reference-blocks/use-code-reference-highlight";

export interface DomHighlight {
    id: number | null | undefined;
    ranges: Range[];
}

export interface PersistedDomHighlight extends DomHighlight {
    id: number;
}

export const DATA_NO_HIGHLIGHT = "data-no-highlight";

const isTextSelected = () => {
    return !!window.getSelection()?.toString().length;
};

const getHighlighName = (color: HighlightColor, id: number | undefined) => {
    return `${getHighlightColor(color)}-${id}`;
};

const highlightableNodeFilter = (node: Node) => {
    const element = node.parentNode as Element;
    const style = window.getComputedStyle(element);
    if (style.userSelect === "none") {
        return NodeFilter.FILTER_REJECT;
    } else {
        return NodeFilter.FILTER_ACCEPT;
    }
};

const findSectionIdAndOffsetBasedOnRange = (
    container: Element | null | undefined,
    nodeFromRange: Node,
    offsetFromRange: number,
    attributeName: string
) => {
    let sectionNode: Element | undefined;
    let currentNode: Element | undefined | null = container;
    while (!sectionNode && currentNode) {
        if (currentNode.hasAttribute(attributeName)) {
            sectionNode = currentNode;
            break;
        } else {
            currentNode = currentNode.parentElement;
        }
    }

    if (!sectionNode) return { sectionId: null, offsetFromSection: null };

    const offsetFromSection = getCharacterOffset(
        sectionNode,
        nodeFromRange,
        offsetFromRange
    );

    const sectionId = parseInt(sectionNode?.getAttribute(attributeName)!);

    return { sectionId, offsetFromSection };
};

const getCharacterOffset = (
    sectionElement: Element,
    targetNode: Node,
    targetOffset: number
): number => {
    const treeWalker = document.createTreeWalker(
        sectionElement,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode: highlightableNodeFilter,
        }
    );

    let charCount = 0;
    let currentNode: Node | null;

    while ((currentNode = treeWalker.nextNode())) {
        if (currentNode === targetNode) {
            return charCount + targetOffset;
        }
        charCount += currentNode.textContent?.length || 0;
    }

    return charCount;
};

const findDomNodeAndOffsetToBuildRange = (
    sectionToSearch: Element,
    offsetFromSection: number
) => {
    let currentText = "";
    let offsetForRange = 0;
    let nodeForRange: Node | null = null;

    const treeWalker = document.createTreeWalker(
        sectionToSearch,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode: highlightableNodeFilter,
        }
    );
    const allTextNodes: Node[] = [];
    let currentNode = treeWalker.nextNode();
    while (currentNode) {
        allTextNodes.push(currentNode);
        currentNode = treeWalker.nextNode();
    }

    for (const element of allTextNodes) {
        const prevCurrentText = currentText;
        currentText = currentText + (element.textContent ?? "");
        if (currentText.length >= offsetFromSection) {
            nodeForRange = element;
            offsetForRange = offsetFromSection - prevCurrentText.length;
            break;
        }
    }

    return {
        nodeForRange,
        offsetForRange,
    };
};

const getRangeFromCurrentSelection = () => {
    const selection = window.getSelection();
    if (!selection?.toString().length) {
        return;
    }
    return selection?.getRangeAt(0);
};

/** Returns the CSS color string for a given HighlightColor enum value; e.g. "yellow" */
const getHighlightColor = (color: HighlightColor): string => {
    return HighlightColor[color].toLowerCase();
};

const registerCSSHighlightFromRange = (
    uniqueHighlightName: string,
    ranges: Range[],
    priority?: number
) => {
    const currentHighlightsInRegistery =
        CSS.highlights.get(uniqueHighlightName);
    if (!currentHighlightsInRegistery) {
        const highlight = new Highlight(...ranges);
        CSS.highlights.set(uniqueHighlightName, highlight);
        highlight.priority = priority ?? 0;
    } else {
        ranges.forEach((range) => {
            currentHighlightsInRegistery.add(range);
        });
    }
};

const deleteFromHighlightRegistry = (range: Range) => {
    CSS.highlights.forEach((highlight) => highlight.delete(range));
};

const removeHighlightFromRegistry = (name: string, ranges: Range[]) => {
    const highlight = CSS.highlights.get(name);
    if (!highlight) return;
    ranges.forEach((range) => highlight.delete(range));
};

// const removeRangesFromHighlight = (color: HighlightColor, ranges: Range[]) => {
//     const colorName = getHighlightColor(color);
//     const highlight = CSS.highlights.get(colorName);
//     if (!highlight) return;
//     ranges.forEach((range) => highlight.delete(range));
// };

const moveHighlightToNewRegistry = (
    ranges: Range[],
    oldColor: HighlightColor,
    newColor: HighlightColor,
    priority: number,
    id: number | undefined
) => {
    removeHighlightFromRegistry(getHighlighName(oldColor, id), ranges);
    const newColorName = getHighlighName(newColor, id);
    registerCSSHighlightFromRange(newColorName, ranges, priority);
    addTemporaryHighlightStyleToExistingCSS(newColor, id);
};

// Rewrite to handle blue-new to orange-new, add for real instead of adding to (now definct) color registry

const attachClickHandler = (
    ref: HTMLDivElement,
    getHighlights: () => PersistedDomHighlight[],
    handleHighlightClick: (
        highlightId: number,
        coordinates?: { x: number; y: number }
    ) => void
) => {
    const handleClick = (e: MouseEvent) => {
        // Bail if anchor or button so that click handler can take priority
        if (
            e.target instanceof HTMLAnchorElement ||
            e.target instanceof HTMLButtonElement
        ) {
            return;
        }
        const caretPosition = document.caretPositionFromPoint(
            e.clientX,
            e.clientY
        );
        if (!caretPosition) return;

        const { offsetNode: clickedNode, offset: clickedOffset } =
            caretPosition;

        const highlights = getHighlights();

        for (const highlight of highlights) {
            if (
                !highlight.ranges.some((range) =>
                    range.isPointInRange(clickedNode, clickedOffset)
                )
            ) {
                continue;
            }
            const coordinates = getCoordinateBasedOnRef(ref, e);
            handleHighlightClick(highlight.id, coordinates);
            e.stopImmediatePropagation();
            break;
        }
    };

    ref.addEventListener("click", handleClick);

    return () => {
        ref.removeEventListener("click", handleClick);
    };
};

const doesElementOrChildrenHaveHighlights = (e) => {
    const selection = window.getSelection();
    const node = e.currentTarget;
    return (
        (node &&
            !selection?.isCollapsed &&
            !!selection?.anchorNode &&
            selection.containsNode(node, true)) ??
        false
    );
};

const clearSelection = () => {
    window.getSelection()?.removeAllRanges();
};

const getFilteredRanges = (range: Range, excludedSelector: string): Range[] => {
    const isOneNode = range.startContainer === range.endContainer;
    if (isOneNode) return [range];

    const doc = range.commonAncestorContainer.ownerDocument;
    if (!doc) return [];
    const walker = doc.createTreeWalker(
        range.commonAncestorContainer,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode(node) {
                if (!range.intersectsNode(node)) {
                    return NodeFilter.FILTER_REJECT;
                }

                if (node.parentElement?.closest(excludedSelector)) {
                    return NodeFilter.FILTER_REJECT;
                }

                if (!node.textContent?.trim()) {
                    return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
            },
        }
    );
    const result: Range[] = [];

    let textNode: Node | null = null;
    while ((textNode = walker.nextNode())) {
        const subRange = doc.createRange();
        subRange.selectNodeContents(textNode);

        // clamp to original range boundaries
        if (textNode === range.startContainer) {
            subRange.setStart(textNode, range.startOffset);
        }
        if (textNode === range.endContainer) {
            subRange.setEnd(textNode, range.endOffset);
        }

        result.push(subRange);
    }

    return result;
};

const buildRangeFromHighlightRecord = (
    refElement: Element,
    highlightRecord: CodeReferenceBlockHighlightRecord
) => {
    // Get the sections as starting points for node-hunting
    const startingSection = refElement.querySelector(
        `[${DATA_SECTION_ID}="${highlightRecord.startingSectionId}"]`
    );
    const endingSection = refElement.querySelector(
        `[${DATA_SECTION_ID}="${highlightRecord.endingSectionId}"]`
    );

    if (!startingSection || !endingSection) return [];

    // Get nodes, offset
    const { nodeForRange: startNode, offsetForRange: startOffset } =
        HighlightUtils.findDomNodeAndOffsetToBuildRange(
            startingSection,
            highlightRecord.startingSectionOffset
        );
    const { nodeForRange: endNode, offsetForRange: endOffset } =
        HighlightUtils.findDomNodeAndOffsetToBuildRange(
            endingSection,
            highlightRecord.endingSectionOffset
        );

    // Maybe throw an exception
    if (!startNode || !endNode) {
        console.error("Unable to find start or end node");
        return [];
    }

    // Create range
    const range = document.createRange();
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);

    const filteredRanges = HighlightUtils.getFilteredRanges(
        range,
        `[${DATA_NO_HIGHLIGHT}]`
    );

    return filteredRanges;
};

const getStyleRule = (
    highlightColor: HighlightColor,
    id: number | undefined
) => {
    const color = HighlightUtils.getHighlightColor(highlightColor);
    return `
            ::highlight(${color}-${id}) {
                background-color: var(--highlight-${color});
            }
        `;
};

const applyCustomCssToMatchDynamicHighlightNames = (
    highlightRecords: CodeReferenceBlockHighlightRecord[]
) => {
    const highlightRules = highlightRecords.reduce(
        (styleString, currentHighlight) => {
            return (
                styleString +
                getStyleRule(
                    currentHighlight.highlightColor,
                    currentHighlight.id
                )
            );
        },
        ""
    );

    // Appends it to <head> so that ::highlight() will be scoped properly
    const styleElement = document.createElement("style");
    styleElement.textContent = highlightRules;
    const existingStyleElement = document.getElementById("highlights");
    if (existingStyleElement) {
        existingStyleElement.replaceWith(styleElement);
    } else {
        document.head.appendChild(styleElement);
    }
};

const addTemporaryHighlightStyleToExistingCSS = (
    highlightColor: HighlightColor,
    id: number | undefined
) => {
    const tempHighlightRule = getStyleRule(highlightColor, id);

    const existingStyleElement = document.getElementById("highlights");

    if (existingStyleElement) {
        existingStyleElement.textContent =
            existingStyleElement.textContent + "" + tempHighlightRule;
    } else {
        const styleElement = document.createElement("style");
        styleElement.textContent = tempHighlightRule;
        document.head.appendChild(styleElement);
    }
};

// Used to normal a range and remove any empty spans that might have been caught.
// Was causing Bug #2 in https://nfpa.atlassian.net/browse/LINK-1885
const normalizeRange = () => {
    const selection = window.getSelection();
    if (selection?.isCollapsed || selection?.rangeCount === 0) return;
    const range = selection?.getRangeAt(0);

    if (range?.endContainer.nodeType === Node.ELEMENT_NODE) {
        const walker = document.createTreeWalker(
            range.commonAncestorContainer,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: (node) => {
                    return !!node.textContent &&
                        node.textContent.trim().length > 0
                        ? NodeFilter.FILTER_ACCEPT
                        : NodeFilter.FILTER_REJECT;
                },
            }
        );

        walker.currentNode = range.endContainer;
        const lastTextNode = walker.previousNode() as Text;

        if (lastTextNode) {
            range.setEnd(lastTextNode, lastTextNode.length);

            selection?.removeAllRanges();
            selection?.addRange(range);
        }
    }
};

const getAllTextNodes = (rootElement: Element) => {
    const allTextNodes: Node[] = [];

    const treeWalker = document.createTreeWalker(
        rootElement,
        NodeFilter.SHOW_TEXT
    );
    let currentNode = treeWalker.nextNode();
    while (currentNode) {
        allTextNodes.push(currentNode);
        currentNode = treeWalker.nextNode();
    }

    return allTextNodes;
};

const buildRangesFromSectionSearchResult = (
    parentRef: RefObject<HTMLElement | undefined>,
    externalId: string,
    searchTerm: string
): Range[] => {
    const sectionElement = document.getElementById(externalId);
    if (sectionElement) {
        const allTextNodesToSearch = getAllTextNodes(sectionElement);
        const ranges = allTextNodesToSearch.flatMap((el) => {
            const text = el.textContent?.toLowerCase();

            const wordToSearch = searchTerm.trim().toLowerCase();
            const indices: number[] = [];
            let startPos = 0;
            while (startPos < text!.length) {
                const index = text!.indexOf(wordToSearch, startPos);
                if (index === -1) break;
                indices.push(index);
                startPos = index + wordToSearch.length;
            }

            // Create a range object for each instance of
            // wordToSearch we found in the text node.
            return indices.map((index) => {
                const range = new Range();
                range.setStart(el, index);
                range.setEnd(el, index + wordToSearch.length);
                return range;
            });
        });
        return ranges;
    }

    return [];
};

const buildRangesForEntireBookviewBasedOnSearchTerm = (
    parentRef: RefObject<HTMLElement | undefined>,
    searchTerm: string
): Range[] => {
    const contentElement = parentRef.current;
    if (contentElement) {
        const allTextNodesToSearch = getAllTextNodes(contentElement);
        const ranges = allTextNodesToSearch.flatMap((el) => {
            const text = el.textContent?.toLowerCase();

            const wordToSearch = searchTerm.trim().toLowerCase();
            const indices: number[] = [];
            let startPos = 0;
            while (startPos < text!.length) {
                const index = text!.indexOf(wordToSearch, startPos);
                if (index === -1) break;
                indices.push(index);
                startPos = index + wordToSearch.length;
            }

            // Create a range object for each instance of
            // wordToSearch we found in the text node.
            return indices.map((index) => {
                const range = new Range();
                range.setStart(el, index);
                range.setEnd(el, index + wordToSearch.length);
                return range;
            });
        });
        return ranges;
    }

    return [];
};

const HighlightUtils = {
    attachClickHandler,
    buildRangeFromHighlightRecord,
    buildRangesFromSectionSearchResult,
    buildRangesForEntireBookviewBasedOnSearchTerm,
    clearSelection,
    deleteFromHighlightRegistry,
    doesElementOrChildrenHaveHighlights,
    findDomNodeAndOffsetToBuildRange,
    findSectionIdAndOffsetBasedOnRange,
    getFilteredRanges,
    getHighlightColor,
    getRangeFromCurrentSelection,
    isTextSelected,
    moveHighlightToNewRegistry,
    registerCSSHighlightFromRange,
    applyCustomCssToMatchDynamicHighlightNames,
    removeHighlightFromRegistry,
    getHighlighName,
    normalizeRange,
};

const getCoordinateBasedOnRef = (
    ref: HTMLDivElement,
    e: MouseEvent
): { x: number; y: number } => {
    const rect = ref.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    return { x, y };
};

export default HighlightUtils;
